import { safeSessionStorage } from '@/lib/safeStorage';

// One place decides what happens when a dynamic import fails, which is what a
// deploy landing mid-session looks like from the browser: the chunk hash the
// running bundle asks for is gone, the import rejects above every error
// boundary, and the user is left on a blank screen.
//
// Two callers share this guard on purpose. `lazyWithRetry` in App.tsx wraps the
// routes, and the vite:preloadError listener registered below catches the
// dynamic imports that are not routes. When they each kept their own guard they
// could both fire for a single failure and reload twice (RUT-3216).
//
// Guard lives in sessionStorage so it survives the router's <Navigate replace>
// redirects on boot, which strip URL query params and previously reset a
// query-param-only guard, causing an endless reload loop (RUT-2928). The URL
// param is kept as a fallback for when storage is blocked (e.g. Safari private
// mode, hardened browsers) and sessionStorage silently no-ops.
const CHUNK_RELOAD_GUARD_KEY = 'chunk_reload_attempted';

// How long to keep suspending before concluding the reload is not coming. A
// beforeunload handler can prompt "Leave site?" and the user can choose to
// stay, which cancels the navigation — without this, every later chunk failure
// would suspend on a spinner forever with nothing reaching Sentry.
const RELOAD_ABANDONED_MS = 10_000;

// True once a reload has been started but the document has not been replaced
// yet. Callers use it to keep rendering the spinner rather than surfacing an
// error for a page that is already on its way out.
let reloadInFlight = false;

export function isChunkReloadInFlight(): boolean {
  return reloadInFlight;
}

export function hasAttemptedChunkReload(): boolean {
  const url = new URL(window.location.href);
  return (
    safeSessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY) === '1' ||
    url.searchParams.get('chunk_reload') === '1'
  );
}

/**
 * Reload once to pick up the new index.html. Returns false if a reload is
 * already in flight or was already attempted this session, in which case the
 * caller should let the error surface normally so it reaches Sentry.
 */
export function attemptChunkReload(): boolean {
  if (reloadInFlight || hasAttemptedChunkReload()) return false;
  reloadInFlight = true;
  setTimeout(() => {
    reloadInFlight = false;
  }, RELOAD_ABANDONED_MS);

  safeSessionStorage.setItem(CHUNK_RELOAD_GUARD_KEY, '1');
  const url = new URL(window.location.href);
  url.searchParams.set('chunk_reload', '1');
  window.location.href = url.toString();
  return true;
}

/**
 * Catches the dynamic imports that are not routes — modals, panels, and the
 * `await import(...)` call sites — which no React.lazy wrapper sees.
 *
 * Deliberately does NOT call event.preventDefault(). Cancelling the event does
 * not just suppress Vite's rethrow; it makes the failed import RESOLVE with
 * undefined, and React.lazy then reads `.default` off undefined and throws
 * during render, unmounting the tree into the white screen this exists to
 * prevent. Leaving the event live keeps the import rejected, so lazyWithRetry
 * can suspend on it while the reload lands.
 */
export function registerChunkReloadListener(): void {
  window.addEventListener('vite:preloadError', () => {
    attemptChunkReload();
  });
}
